import React, {
    lazy, Suspense,
    useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { renderRoutes } from 'react-router-config';
import { Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { createBrowserHistory } from 'history';

import { NotificationProvider } from 'context/NotificationContext';

import { isEngineN1tro, isEngineTmgaming } from 'common/shared';

import 'babel-polyfill';

import APIUtils from './apis/APIUtils';
import AuthAPI from './apis/AuthAPI';
import { loginRoutes, routes } from './common/routes/routes';
import SocketComponent from './SocketComponent';

import 'react-toastify/dist/ReactToastify.css';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import 'bootstrap-daterangepicker/daterangepicker.css';
import 'common/scss/App.scss';
const InformModal = lazy(() => import('components/InformModal/InformModal'));
const loading = () => <div className="animated fadeIn pt-3 text-center"><div className="sk-spinner sk-spinner-pulse" /></div>;

const history = createBrowserHistory();

if (process.env.NODE_ENV === 'development') {
    /* warning in react 18 */
    const originalError = console.error;

    console.error = (...args) => {
        if (/Warning: .*defaultProps will be removed/.test(args[0])) {
            return;
        }
        if (/Warning: .*value and defaultValue props/.test(args[0])) {
            return;
        }

        if (/Warning: findDOMNode is deprecated/.test(args[0])) {
            return;
        }

        if (/Warning: Unexpected return value from a callback ref/.test(args[0])) {
            return;
        }

        if (/Warning: A props object containing a "key" prop is being spread into JSX/.test(args[0])) {
            return;
        }

        originalError(...args);
    };
}

const App = () => {
    const [r, setRoutes] = useState([]);
    const token = useSelector(state => state.auth.token);
    const isReloadPage = useSelector(state => state.status.isReloadPage);
    const [firstToken, setFirstToken] = useState(token);
    const [currentUser, setCurrentUser] = useState(JSON.parse(localStorage.getItem('user')));
    const initialNotification = {
        deposit: 0,
        withdraw: 0,
        pagaWithdraw: 0,
        kyc: 0,
        memo: 0,
        needUpdate: false,
        logNeedUpdate: false,
        repeatSound: true,
        IsCleaningUp: false,
        outstandingBalance: 0,
        availableCreditLimit: 0,
        isBillUpdated: false,
        totalOutstanding: 0,
        totalCreditLimit: 0,
        onlineMember: 0,
        fakeOnline: 0,
        websiteStatus: '',
        lastUpdate: '',
        runningText: [],
        notifyActionLog: 0,
        autoBankNotification: 0,
        trackingClonedCount: 0,
    };

    const notificationReducer = (state, action) => {
        switch (action.type) {
            case 'update_notification':
                return {
                    ...state,
                    has_new_request: action.has_new_request,
                    deposit: action.deposit,
                    withdraw: action.withdraw,
                    memo: action.memo,
                    pagaWithdraw: action.paga_withdraw_count,
                    trackingClonedCount: action.tracking_cloned_count,
                    runningText: action.running_text,
                    kyc: action.kyc,
                    request: null,
                };
            case 'reconnect_notification': {
                return {
                    ...state,
                    needUpdate: !state.needUpdate,
                    request: null,
                };
            }
            case 'insert':
            case 'update':
            case 'remove':
                return {
                    ...state,
                    ...action
                };
            case 'update_repeat_sound':
                return {
                    ...state,
                    repeatSound: action.repeatSound
                };
            case 'is_cleaning_up_process':
                return {
                    ...state,
                    IsCleaningUp: action.IsCleaningUp
                };
            case 'log_need_update': {
                return {
                    ...state,
                    logNeedUpdate: !state.logNeedUpdate,
                    notifyActionLog: action.total_notify_action_log,
                    request: null,
                };
            }
            case 'log_need_update_autobank': {
                return {
                    ...state,
                    logNeedUpdate: !state.logNeedUpdate,
                    autoBankNotification: action.total_autobank_notification,
                    request: null,
                };
            }
            case 'pagadw_update_notify': {
                return {
                    ...state,
                    pagaWithdraw: action.withdraw_count,
                    request: null,
                };
            }
            case 'kyc_pending': {
                return {
                    ...state,
                    kyc: action.total_pending,
                    request: null,
                };
            }

            case 'memo_need_update': {
                return {
                    ...state,
                    memo: action.unread,
                    request: null,
                };
            }

            case 'top_bar_menu_tracking_cloned_notify': {
                return {
                    ...state,
                    trackingClonedCount: action.total_notifications,
                    request: null,
                };
            }
            case 'top_bar_menu_running_text': {
                return {
                    ...state,
                    runningText: action.running_text || [],
                    request: null,
                };
            }

            case 'update_totalbill_balance': {
                return {
                    ...state,
                    outstandingBalance: action.outstandingBalance,
                    availableCreditLimit: action.availableCreditLimit,
                    totalOutstanding: action.totalOutstanding,
                    totalCreditLimit: action.totalCreditLimit,
                    websiteStatus: action.websiteStatus,
                    lastUpdate: action.lastUpdate,
                    onlineMember: action.onlineMember,
                    fakeOnline: action.fakeOnline,
                    isBillUpdated: true,
                    request: null,
                };
            }

            default:
                return state;
        }
    };

    useEffect(() => {
        if (firstToken && firstToken !== token) {
            if (token === '') {
                window.location.href = '/login';
            } else {
                window.location.reload();
            }
        }
    }, [token, firstToken]);

    useEffect(() => {
        if (!firstToken) {
            setFirstToken(token);
        }
    }, [token, setFirstToken]);

    useEffect(() => {
        if (APIUtils.isAuthed()) {
            AuthAPI.authorize().then((user) => {
                setCurrentUser(user);
                return user;
            }).then((user) => {
                routes(user).then((rr) => {
                    setRoutes(rr);
                });
            });
        } else {
            setRoutes(loginRoutes);
        }
    }, []);

    const favicon = useMemo(() => {
        if (isEngineTmgaming) {
            return require('assets/img/brand/tmgaming/favicon.ico');
        }
        if (isEngineN1tro) {
            return require('assets/img/brand/n1tro/favicon.ico');
        }
        return require('assets/img/brand/favicon.ico');
    }, [window.engine]);

    return (
        <div id='App'>
            <Helmet>
                <link rel="shortcut icon" href={favicon} />
            </Helmet>
            <NotificationProvider initialState={initialNotification} reducer={notificationReducer}>
                <Router history={history}>
                    <Suspense fallback={loading()}>
                        <ToastContainer
                            position="top-right"
                            autoClose={5000}
                        />
                        <SocketComponent soundData={currentUser?.sound_setting} />
                        <InformModal
                            open={isReloadPage}
                        />
                        {renderRoutes(r, { currentUser: currentUser })}
                    </Suspense>
                </Router>
            </NotificationProvider>
        </div>
    );
};

export default App;

